import React, { useMemo, useRef } from "react"
import { cva } from "class-variance-authority"
import { Label, type LabelProps } from "./Label"
import cn from "../../../utils/classes"

export interface ToggleProps {
  /**
   * `id` is required and will be applied to the native checkbox allowing
   * a custom `<label htmlFor={id}>` elsewhere to correctly target it.
   * otherwise an internal label element can be rendered through the `labelProps`
   */
  id: string
  toggleState: boolean
  /**
   * Props for an optional label to be rendered alongside the toggle
   */
  labelProps?: Omit<LabelProps, "htmlFor">
  onToggle?: (value: boolean) => void
  disabled?: boolean
  readOnly?: boolean
  styleOverrides?: string
}

const Toggle: React.FC<ToggleProps> = ({
  labelProps,
  toggleState = false,
  onToggle,
  styleOverrides,
  disabled = false,
  readOnly = false,
  id,
}) => {
  const inputRef = useRef<HTMLInputElement>(null)

  const handleToggle:
    | React.ChangeEventHandler<HTMLInputElement>
    | React.MouseEventHandler<HTMLLabelElement> = (event: unknown) => {
    if (!disabled && !readOnly) {
      const value =
        (event as React.ChangeEvent<HTMLInputElement>).target?.checked ??
        /** If the Label is the trigger then get the value from the input */
        inputRef.current?.checked
      onToggle?.(!!value)
    }
  }

  const rootVariants = cva(
    [
      "relative",
      "max-w-content",
      "h-2",
      "flex",
      "items-center",
      "rounded-full",
      "p-2",
      "cursor-pointer",
      // show a visible ring when the native input inside receives focus
      "focus-within:outline-none",
      "focus-within:ring-2",
      "focus-within:ring-[#2198CC]",
      "focus-within:ring-offset-2",
      "focus-within:ring-offset-white",
      "active:outline-none",
      "active:ring-2",
      "active:ring-[#2198CC]",
      "active:ring-offset-2",
      "active:ring-offset-white",
    ],
    {
      variants: {
        toggledOn: {
          true: "bg-primary-blue border-solid border-1/4 border-blue-600",
          false: "bg-gray-200 border-solid border-1/4 border-gray-200",
        },
        disabled: {
          true: "cursor-not-allowed",
        },
        readOnly: {
          true: "!cursor-not-allowed",
        },
      },
    },
  )

  const sliderVariants = cva(
    [
      "absolute",
      "w-5",
      "h-5",
      "transition-all",
      "duration-300",
      "rounded-full",
      "bg-white",
    ],
    {
      variants: {
        toggledOn: {
          true: "right-1",
          false: "left-1",
        },
      },
    },
  )

  const textContainerVariants = cva(
    ["w-full", "text-center", "transition-all", "duration-300", "text-white"],
    {
      variants: {
        toggledOn: {
          true: "left-1 pr-6 pl-2",
          false: "right-1 pl-6 pr-2",
        },
      },
    },
  )

  const rootClassName = useMemo(() => {
    return cn(
      rootVariants({ toggledOn: toggleState, disabled, readOnly }),
      styleOverrides,
    )
  }, [styleOverrides, toggleState, rootVariants, disabled, readOnly])

  const sliderClassName = useMemo(() => {
    return cn(sliderVariants({ toggledOn: toggleState }))
  }, [sliderVariants, toggleState])

  const textContainerClassName = useMemo(() => {
    return cn(textContainerVariants({ toggledOn: toggleState }))
  }, [textContainerVariants, toggleState])

  return (
    // Use a native checkbox input (visually hidden) for semantics/labels and
    // keep the original visual structure for styling.
    <>
      <label
        className={rootClassName}
        aria-disabled={disabled}
        aria-readonly={readOnly}
      >
        <input
          id={id}
          ref={inputRef}
          type="checkbox"
          className="sr-only"
          checked={toggleState}
          onChange={handleToggle as React.ChangeEventHandler<HTMLInputElement>}
          disabled={disabled}
          readOnly={readOnly}
          aria-checked={toggleState}
          aria-readonly={readOnly}
        />

        {/* Visual parts of the switch (decorative) */}
        <div className={sliderClassName} aria-hidden="true" />
        <div className={textContainerClassName} aria-hidden="true" />
      </label>
      {labelProps?.text && (
        <Label
          {...labelProps}
          size={labelProps!.size || "sm"}
          htmlFor={id ?? ""}
          styleOverrides={cn(labelProps.styleOverrides, {
            "cursor-pointer": !readOnly,
          })}
        />
      )}
    </>
  )
}

const toggle = React.memo(Toggle)
export { toggle as Toggle }
